// frontend/src/components/communityBasedAssessment/CommunityBasedAssessmentView.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const CommunityBasedAssessmentView = ({
  assessmentId,
  onBack,
  onEdit,
  onGeneratePDF,
}) => {
  const { tenant_id: tenantId } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const viewTopRef = useRef(null);

  useEffect(() => {
    if (!assessmentId || !tenantId) return;

    setLoading(true);
    secureAxios
      .get(`${apiBase}/api/community-based-assessments/${assessmentId}`, {
        params: { tenant_id: tenantId },
      })
      .then((res) => setAssessment(res.data))
      .catch((err) => {
        console.error("Error loading assessment:", err);
        toast.error("Failed to load assessment");
      })
      .finally(() => setLoading(false));
  }, [assessmentId, tenantId, apiBase]);

  // Force layout recalculation AFTER loading completes
  useEffect(() => {
    if (!loading && assessment) {
      // Small delay to ensure DOM has rendered
      const timer = setTimeout(() => {
        if (viewTopRef.current) {
          viewTopRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
        window.dispatchEvent(new Event('resize'));
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [loading, assessment]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="alert alert-warning">
        Assessment not found.
        <button className="btn btn-link" onClick={onBack}>
          Go Back
        </button>
      </div>
    );
  }

  // Helper to render an assessment item with its summary
  const renderAssessmentItem = (label, value, summary) => (
    <div className="card mb-3">
      <div className="card-header bg-light py-2">
        <strong>{label}</strong>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-5">
            <small className="text-muted d-block mb-1">Functional Assessment Data</small>
            <span className={value ? "fw-medium" : "text-muted"}>
              {value || "Not assessed"}
            </span>
          </div>
          <div className="col-md-7">
            <small className="text-muted d-block mb-1">Assessment Summary</small>
            <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {summary || <span className="text-muted fst-italic">No summary provided</span>}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container my-4">
    <div className="community-based-assessment-view" ref={viewTopRef} style={{ minWidth: 0, overflow: 'visible' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-1">
            <i className="bi bi-clipboard-data me-2"></i>
            CRP-21: Community Based Assessment
          </h4>
          <p className="text-muted mb-0">
            Viewing assessment for {assessment.consumer_name}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={onBack}>
            <i className="bi bi-arrow-left me-1"></i>
            Back to List
          </button>
          <button className="btn btn-outline-primary" onClick={onEdit}>
            <i className="bi bi-pencil me-1"></i>
            Edit
          </button>
          <button className="btn btn-info" onClick={onGeneratePDF}>
            <i className="bi bi-file-pdf me-1"></i>
            Generate PDF
          </button>
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-4">
        <span
          className={`badge ${
            assessment.status === "completed"
              ? "bg-success"
              : assessment.status === "archived"
              ? "bg-secondary"
              : "bg-warning text-dark"
          } fs-6`}
        >
          {assessment.status === "completed"
            ? "Completed"
            : assessment.status === "archived"
            ? "Archived"
            : "Draft"}
        </span>
      </div>

      {/* Header Information */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            Header Information
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <strong>Consumer:</strong> {assessment.consumer_name}
              </div>
              <div className="mb-3">
                <strong>Person Completing Form:</strong> {assessment.completed_by_name || "-"}
              </div>
              <div className="mb-3">
                <strong>Assessment Site:</strong>{" "}
                {assessment.assessment_site_name || assessment.assessment_site_text || "-"}
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <strong>Dates/Hours:</strong> {assessment.dates_hours || "-"}
              </div>
              <div className="mb-3">
                <strong>Total Hours:</strong> {assessment.total_hours || "-"}
              </div>
              <div className="mb-3">
                <strong>Created:</strong> {formatDate(assessment.created_at)}
              </div>
              {assessment.authorization_number && (
                <div className="mb-3">
                  <strong>Authorization:</strong> {assessment.authorization_number}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Page 1: Physical Assessment */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-1-circle me-2"></i>
            Page 1: Physical Assessment
          </h5>
        </div>
        <div className="card-body">
          {renderAssessmentItem("Strength, Lifting and Carrying", assessment.strength_lifting, assessment.strength_lifting_summary)}
          {renderAssessmentItem("Endurance", assessment.endurance, assessment.endurance_summary)}
          {renderAssessmentItem("Orienting", assessment.orienting, assessment.orienting_summary)}
          {renderAssessmentItem("Physical Mobility", assessment.physical_mobility, assessment.physical_mobility_summary)}
        </div>
      </div>

      {/* Page 2: Work Behaviors */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-2-circle me-2"></i>
            Page 2: Work Behaviors
          </h5>
        </div>
        <div className="card-body">
          {renderAssessmentItem("Independent Work Rate", assessment.work_rate, assessment.work_rate_summary)}
          {renderAssessmentItem("Appearance", assessment.appearance, assessment.appearance_summary)}
          {renderAssessmentItem("Communication", assessment.communication, assessment.communication_summary)}
          {renderAssessmentItem("Social Interactions", assessment.social_interactions, assessment.social_interactions_summary)}
          {renderAssessmentItem("Attention to Task/Perseverance", assessment.attention_perseverance, assessment.attention_perseverance_summary)}
        </div>
      </div>

      {/* Page 3: Job Performance */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-3-circle me-2"></i>
            Page 3: Job Performance
          </h5>
        </div>
        <div className="card-body">
          {renderAssessmentItem("Independent Sequencing of Job Duties", assessment.job_sequencing, assessment.job_sequencing_summary)}
          {renderAssessmentItem("Initiative/Motivation", assessment.initiative_motivation, assessment.initiative_motivation_summary)}
          {renderAssessmentItem("Adapting to Change", assessment.adapting_change, assessment.adapting_change_summary)}
          {renderAssessmentItem("Reinforcement Needs", assessment.reinforcement_needs, assessment.reinforcement_needs_summary)}
          {renderAssessmentItem("Level of Support", assessment.level_support, assessment.level_support_summary)}
        </div>
      </div>

      {/* Page 4: Cognitive Skills */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-4-circle me-2"></i>
            Page 4: Cognitive Skills
          </h5>
        </div>
        <div className="card-body">
          {renderAssessmentItem("Discrimination Skills", assessment.discrimination_skills, assessment.discrimination_skills_summary)}
          {renderAssessmentItem("Time Awareness", assessment.time_awareness, assessment.time_awareness_summary)}
          {renderAssessmentItem("Functional Reading", assessment.functional_reading, assessment.functional_reading_summary)}
          {renderAssessmentItem("Functional Math", assessment.functional_math, assessment.functional_math_summary)}
          {renderAssessmentItem("Independent Street Crossing", assessment.street_crossing, assessment.street_crossing_summary)}
          {renderAssessmentItem("Handling Criticism/Stress", assessment.handling_criticism, assessment.handling_criticism_summary)}
        </div>
      </div>

      {/* Page 5: Behavioral Concerns */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-5-circle me-2"></i>
            Page 5: Behavioral Concerns
          </h5>
        </div>
        <div className="card-body">
          {renderAssessmentItem("Disruptive Behavior", assessment.disruptive_behavior, assessment.disruptive_behavior_summary)}
          {renderAssessmentItem("Repetitive Behavior/Habits", assessment.repetitive_behavior, assessment.repetitive_behavior_summary)}
          {renderAssessmentItem("Socially Offensive Behavior", assessment.offensive_behavior, assessment.offensive_behavior_summary)}
          {renderAssessmentItem("Withdrawal/Inattentive Behavior", assessment.withdrawal_behavior, assessment.withdrawal_behavior_summary)}
          {renderAssessmentItem("Noncompliant Behavior", assessment.noncompliant_behavior, assessment.noncompliant_behavior_summary)}
        </div>
      </div>

      {/* Page 6: Additional Skills & Recommendations */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-6-circle me-2"></i>
            Page 6: Additional Skills & Recommendations
          </h5>
        </div>
        <div className="card-body">
          {renderAssessmentItem("Money Skills", assessment.money_skills, assessment.money_skills_summary)}
          {renderAssessmentItem("Asking for Assistance", assessment.asking_assistance, assessment.asking_assistance_summary)}

          <div className="card mt-4">
            <div className="card-header bg-light py-2">
              <strong>Other Observations and/or Recommendations</strong>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">Data</label>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {assessment.other_observations || (
                      <span className="text-muted fst-italic">No observations provided</span>
                    )}
                  </p>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">Assessment Summary</label>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {assessment.other_observations_summary || (
                      <span className="text-muted fst-italic">No summary provided</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="d-flex justify-content-between mt-4">
        <button className="btn btn-outline-secondary" onClick={onBack}>
          <i className="bi bi-arrow-left me-1"></i>
          Back to List
        </button>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={onEdit}>
            <i className="bi bi-pencil me-1"></i>
            Edit Assessment
          </button>
          <button className="btn btn-info" onClick={onGeneratePDF}>
            <i className="bi bi-file-pdf me-1"></i>
            Generate PDF
          </button>
        </div>
      </div>
    </div>
    </div>
  );
};

export default CommunityBasedAssessmentView;